import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useLocalStorageState } from '@helpers/useLocalStorageState';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { AsrProxyService } from '@pages/Admin/AsrProxyConsolePage/api/AsrProxyService';
import { createFetchAsrModelSettingsQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrModelSettings';
import { createFetchAsrModelsQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrModels';
import { createFetchAsrProviderOptionsQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrProviderOptions';
import type {
  AsrCreateTranscriptionRequest,
  AsrCreateTranscriptionResponse,
} from '@pages/Admin/AsrProxyConsolePage/api/types';
import { buildPipelineSelectionRequest } from '@pages/Admin/AsrProxyConsolePage/helpers/buildPipelineSelectionRequest';
import { getInitialProviderOptionValue } from '@pages/Admin/AsrProxyConsolePage/helpers/getInitialProviderOptionValue';
import { getParameterDescription } from '@pages/Admin/AsrProxyConsolePage/helpers/getParameterDescription';
import { getProviderOptionAvailability } from '@pages/Admin/AsrProxyConsolePage/helpers/getProviderOptionAvailability';
import { getProviderOptionEditor } from '@pages/Admin/AsrProxyConsolePage/helpers/getProviderOptionEditor';
import { parseProviderOptionDraft } from '@pages/Admin/AsrProxyConsolePage/helpers/parseProviderOptionDraft';
import type { TPipelineDraft } from '@pages/Admin/AsrProxyConsolePage/types/TPipelineDraft';
import { PipelineSelection } from '../PipelineSelection';

const SPEAKER_COUNT_OPTIONS = ['speakers_expected', 'speaker_options'] as const;
const SAVED_PIPELINE_DRAFT: TPipelineDraft = { normalizers: null, qualityCheckers: null };

type TProps = {
  model: string;
  setModel: Dispatch<SetStateAction<string>>;
  isWaitingForResult: boolean;
  onTranscriptionStart: (response: AsrCreateTranscriptionResponse, audioUrl: string) => void;
  onError: (message: string) => void;
  onParametersOpenChange: (open: boolean) => void;
};

export const SubmitPanel = ({
  model,
  setModel,
  isWaitingForResult,
  onTranscriptionStart,
  onError,
  onParametersOpenChange,
}: TProps) => {
  const { call } = useAPI();
  const [parameterDraft, setParameterDraft] = useState<Record<string, string>>({});
  const [audioUrl, setAudioUrl] = useLocalStorageState('asr-playground:audioUrl', 'https://example.com/audio.mp3');
  const [busy, setBusy] = useState(false);
  const [pipelineDraft, setPipelineDraft] = useState<TPipelineDraft>(SAVED_PIPELINE_DRAFT);
  const [parametersOpen, setParametersOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);

  const modelsQuery = useQuery({
    queryKey: asrProxyKeys.models(),
    queryFn: createFetchAsrModelsQueryFn(call),
  });
  const providerOptionsQuery = useQuery({
    queryKey: asrProxyKeys.providerOptions(),
    queryFn: createFetchAsrProviderOptionsQueryFn(call),
  });
  const modelSettingsQuery = useQuery({
    queryKey: asrProxyKeys.modelSettings(),
    queryFn: createFetchAsrModelSettingsQueryFn(call),
  });
  const models = modelsQuery.data?.models ?? [];
  const knownOptions = useMemo(() => modelsQuery.data?.known_options ?? [], [modelsQuery.data]);
  const providerMappings = providerOptionsQuery.data?.providers ?? [];
  const defaultModel = modelsQuery.data?.default_model;

  useEffect(() => {
    if (!defaultModel) {
      return;
    }
    setModel((currentModel) =>
      modelsQuery.data?.models.some((candidate) => candidate.model_name === currentModel) ? currentModel : defaultModel,
    );
  }, [defaultModel, modelsQuery.data, setModel]);

  useEffect(() => {
    setPipelineDraft(SAVED_PIPELINE_DRAFT);
  }, [model]);

  useEffect(() => {
    onParametersOpenChange(parametersOpen || pipelineOpen);
  }, [parametersOpen, pipelineOpen, onParametersOpenChange]);

  const transcribeMutation = useMutation({
    mutationFn: (body: AsrCreateTranscriptionRequest) =>
      call(() => AsrProxyService.createTranscription(body), { dontCatchErrors: true }),
  });

  const selectedModel = models.find((row) => row.model_name === model);
  const selectedModelSettings = modelSettingsQuery.data?.find((row) => row.modelName === model);
  const modelSettingsError = modelSettingsQuery.isError
    ? getApiErrorMessage(modelSettingsQuery.error) || 'Could not load model pipeline settings.'
    : '';
  const selectedProviderMappings = providerMappings.find((provider) => provider.provider === selectedModel?.provider);
  const availabilityByOption = useMemo(
    () =>
      Object.fromEntries(
        knownOptions.map((option) => [
          option,
          getProviderOptionAvailability(
            option,
            selectedModel,
            selectedProviderMappings?.mappings.find((mapping) => mapping.option === option),
          ),
        ]),
      ),
    [knownOptions, selectedModel, selectedProviderMappings],
  );

  useEffect(() => {
    if (!selectedModel || !selectedProviderMappings) {
      return;
    }
    setParameterDraft((current) =>
      Object.fromEntries(Object.entries(current).filter(([option]) => availabilityByOption[option]?.available)),
    );
  }, [availabilityByOption, selectedModel, selectedProviderMappings]);

  const options = models
    .filter((row) => row.health?.ok !== false || row.model_name === model)
    .map((row) => ({
      value: row.model_name,
      label: `${row.provider} / ${row.provider_model} — ${row.model_name}${row.health?.ok === false ? ' (down)' : ''}`,
    }));

  const submit = async () => {
    let options: Record<string, unknown>;
    try {
      options = parseProviderOptionDraft(parameterDraft);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
      return;
    }
    setBusy(true);
    onError('');
    try {
      const response = await transcribeMutation.mutateAsync({
        audio_url: audioUrl,
        model,
        options,
        metadata: { source: 'admin-dashboard' },
        async: true,
        ...buildPipelineSelectionRequest(pipelineDraft),
      });
      if (response?.id) {
        onTranscriptionStart(response, audioUrl);
      } else {
        onError('Transcription request failed.');
      }
    } catch (submitError) {
      onError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setBusy(false);
    }
  };

  const hasOption = (option: string) => Object.prototype.hasOwnProperty.call(parameterDraft, option);
  const hasSpeakerCountConflict = SPEAKER_COUNT_OPTIONS.every(hasOption);

  const toggleParameter = (option: string, enabled: boolean) => {
    setParameterDraft((current) => {
      if (!enabled) {
        const remaining = { ...current };
        delete remaining[option];
        return remaining;
      }
      return { ...current, [option]: getInitialProviderOptionValue(option) };
    });
  };

  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)] xl:min-h-0 xl:overflow-y-auto">
      <div className="mb-4">
        <h2 className="text-lg font-medium text-foreground">New transcription</h2>
        <p className="mt-1 text-sm text-foreground-muted">Send an audio file through the selected proxy model.</p>
      </div>
      <Select
        name="playground-model"
        label="Provider / model"
        value={model}
        options={options}
        onChange={(_name, value) => setModel(value)}
        className="form-select w-full"
      />
      <div className="mt-3">
        <Input
          name="playground-audio-url"
          label="Audio URL"
          value={audioUrl}
          onChange={(event) => setAudioUrl(event.target.value)}
        />
      </div>
      <details
        className="group mt-5 border-t border-border pt-4"
        onToggle={(event) => setParametersOpen(event.currentTarget.open)}
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-foreground">
          <span>Request parameter overrides</span>
          <span className="text-xs text-foreground-muted group-open:hidden">Show parameters</span>
          <span className="hidden text-xs text-foreground-muted group-open:inline">Hide parameters</span>
        </summary>
        <p className="mt-2 text-xs text-foreground-muted">
          Enable only values that this request should send explicitly. Other values come from the global defaults in
          Defaults.
        </p>
        {hasSpeakerCountConflict ? (
          <div className="mt-3 rounded-md border border-danger px-3 py-2 text-sm text-danger">
            <code>speakers_expected</code> and <code>speaker_options</code> cannot be sent together
          </div>
        ) : null}
        <div className="mt-3 grid items-stretch gap-3 xl:grid-cols-2">
          {knownOptions.map((option) => {
            const isEnabled = hasOption(option);
            const editor = getProviderOptionEditor(option);
            const availability = availabilityByOption[option] ?? { available: false };
            const conflictingOption =
              option === 'speakers_expected'
                ? 'speaker_options'
                : option === 'speaker_options'
                  ? 'speakers_expected'
                  : undefined;
            const isBlockedByConflict = !isEnabled && conflictingOption !== undefined && hasOption(conflictingOption);
            const isUnavailable = !availability.available;
            return (
              <div
                key={option}
                className={`flex h-full flex-col rounded-md border border-border bg-page p-3 ${
                  isUnavailable ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <CheckBox
                    name={`playground-${option}`}
                    checked={isEnabled}
                    disabled={isBlockedByConflict || isUnavailable}
                    onChange={(enabled) => toggleParameter(option, enabled)}
                  />
                  <div className="min-w-0">
                    <code className="break-words text-xs text-foreground [overflow-wrap:anywhere]">{option}</code>
                    <p className="mt-1 text-xs leading-4 text-foreground-muted">{getParameterDescription(option)}</p>
                    {isUnavailable ? (
                      <p className="mt-1 text-xs leading-4 text-danger">
                        Unavailable for {selectedModel?.model_name ?? 'the selected model'} — {availability.detail}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-auto pt-3">
                  {editor.kind === 'boolean' ? (
                    <Select
                      name={`playground-${option}-value`}
                      label={editor.label}
                      value={parameterDraft[option] ?? ''}
                      options={[
                        { value: 'true', label: 'True' },
                        { value: 'false', label: 'False' },
                      ]}
                      disabled={!isEnabled || isUnavailable}
                      onChange={(_name, value) => setParameterDraft((current) => ({ ...current, [option]: value }))}
                    />
                  ) : (
                    <Input
                      name={`playground-${option}-value`}
                      label={editor.label}
                      type={editor.kind === 'number' ? 'number' : 'text'}
                      inputMode={editor.kind === 'number' ? 'numeric' : 'text'}
                      min={editor.kind === 'number' ? editor.min : undefined}
                      max={editor.kind === 'number' ? editor.max : undefined}
                      multiline={editor.kind === 'text' && editor.multiline}
                      value={parameterDraft[option] ?? ''}
                      disabled={!isEnabled || isUnavailable}
                      error={isEnabled && (!parameterDraft[option] || hasSpeakerCountConflict)}
                      onChange={(event) =>
                        setParameterDraft((current) => ({ ...current, [option]: event.target.value }))
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </details>
      <details
        className="group mt-5 border-t border-border pt-4"
        onToggle={(event) => setPipelineOpen(event.currentTarget.open)}
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-foreground">
          <span>Normalizers and quality checkers</span>
          <span className="text-xs text-foreground-muted group-open:hidden">Show pipeline</span>
          <span className="hidden text-xs text-foreground-muted group-open:inline">Hide pipeline</span>
        </summary>
        <p className="mt-2 text-xs text-foreground-muted">
          Choose which registered normalizers and quality checkers run for this request. Execution order is defined by
          the model; saved settings in Defaults stay unchanged.
        </p>
        <PipelineSelection
          settings={selectedModelSettings}
          isLoading={modelSettingsQuery.isLoading}
          errorMessage={modelSettingsError}
          draft={pipelineDraft}
          onDraftChange={setPipelineDraft}
        />
      </details>
      <Button
        className="mt-4 !w-auto"
        disabled={busy || isWaitingForResult || !model || !audioUrl.trim() || hasSpeakerCountConflict}
        onClick={submit}
      >
        {busy ? 'Submitting…' : isWaitingForResult ? 'Waiting for result…' : 'Transcribe'}
      </Button>
    </section>
  );
};
